import { supabase } from '../lib/supabase';
import { sendApplicationNotifications, getCompanyEmail } from './emailNotificationService';
import { profileService } from './profileService';


/**
 * Job Application Service
 * Handles all application submission, retrieval, and management operations
 * with robust error handling, validation, and duplicate prevention
 */

// ============================================
// ERROR HANDLING & VALIDATION
// ============================================

/**
 * Custom error class for application-specific errors
 */
class ApplicationError extends Error {
  constructor(message, code, details = {}) {
    super(message);
    this.name = 'ApplicationError';
    this.code = code;
    this.details = details;
    this.timestamp = new Date()?.toISOString();
  }
}

/**
 * Validates application data before submission
 * @param {Object} applicationData - Application data to validate
 * @returns {Object} - Validation result with errors if any
 */
const validateApplicationData = (applicationData) => {
  const errors = {};
  
  // Required fields validation
  if (!applicationData?.job_id) {
    errors.job_id = 'Job ID is required';
  }
  
  if (!applicationData?.candidate_id) {
    errors.candidate_id = 'Candidate ID is required';
  }
  
  // Cover letter validation (optional but has length limit)
  if (applicationData?.cover_letter && applicationData?.cover_letter?.length > 5000) {
    errors.cover_letter = 'Cover letter must not exceed 5000 characters';
  }
  
  // Additional data validation
  if (applicationData?.additional_data) {
    try {
      if (typeof applicationData?.additional_data !== 'object') {
        errors.additional_data = 'Additional data must be a valid object';
      }
    } catch (error) {
      errors.additional_data = 'Invalid additional data format';
    }
  }
  
  return {
    isValid: Object.keys(errors)?.length === 0,
    errors
  };
};

/**
 * Formats error response with detailed information
 * @param {Error} error - Error object
 * @param {string} context - Context where error occurred
 * @returns {Object} - Formatted error response
 */
const formatErrorResponse = (error, context) => {
  console.error(`[${context}] Error:`, error);
  
  return {
    success: false,
    error: {
      message: error?.message || 'An unexpected error occurred',
      code: error?.code || 'UNKNOWN_ERROR',
      context,
      details: error?.details || {},
      timestamp: new Date()?.toISOString()
    }
  };
};

// ============================================
// DUPLICATE PREVENTION
// ============================================

/**
 * Checks if user has already applied to a specific job
 * @param {string} jobId - Job ID
 * @param {string} candidateId - Candidate ID
 * @returns {Promise<boolean>} - True if duplicate exists
 */
export const checkDuplicateApplication = async (jobId, candidateId) => {
  try {
    const { data, error } = await supabase?.from('job_applications')?.select('id')?.eq('job_id', jobId)?.eq('candidate_id', candidateId)?.maybeSingle();
    
    if (error) throw error;
    
    return !!data; // Returns true if application exists
  } catch (error) {
    console.error('Error checking duplicate application:', error);
    throw new ApplicationError(
      'Failed to check duplicate application',
      'DUPLICATE_CHECK_ERROR',
      { jobId, candidateId, originalError: error.message }
    );
  }
};

// ============================================
// RESUME UPLOAD & MANAGEMENT
// ============================================

/**
 * Uploads resume file to Supabase Storage
 * @param {File} file - Resume file
 * @param {string} candidateId - Candidate ID
 * @returns {Promise<Object>} - Upload result with file path
 */
export const uploadResume = async (file, candidateId) => {
  try {
    // Validate file
    if (!file) {
      throw new ApplicationError('No file provided', 'INVALID_FILE');
    }
    
    // Check file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file?.size > maxSize) {
      throw new ApplicationError(
        'File size exceeds 5MB limit',
        'FILE_TOO_LARGE',
        { size: file.size, maxSize }
      );
    }
    
    // Check file type
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];
    
    if (!allowedTypes?.includes(file?.type)) {
      throw new ApplicationError(
        'Invalid file type. Only PDF and Word documents are allowed',
        'INVALID_FILE_TYPE',
        { fileType: file.type }
      );
    }
    
    // Generate unique filename
    const timestamp = Date.now();
    const fileExt = file?.name?.split('.')?.pop();
    const fileName = `${candidateId}_${timestamp}.${fileExt}`;
    const filePath = `resumes/${fileName}`;
    
    // Upload to storage
    const { data, error } = await supabase?.storage?.from('resumes')?.upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });
    
    if (error) throw error;
    
    return {
      success: true,
      path: data?.path,
      fileName
    };
  } catch (error) {
    console.error('Error uploading resume:', error);
    throw new ApplicationError(
      'Failed to upload resume',
      'UPLOAD_ERROR',
      { originalError: error.message }
    );
  }
};

/**
 * Gets signed URL for resume download
 * @param {string} resumePath - Path to resume in storage
 * @returns {Promise<string>} - Signed URL
 */
export const getResumeUrl = async (resumePath) => {
  try {
    const { data, error } = await supabase?.storage?.from('resumes')?.createSignedUrl(resumePath, 3600); // 1 hour expiry
    
    if (error) throw error;
    
    return data?.signedUrl;
  } catch (error) {
    console.error('Error getting resume URL:', error);
    throw new ApplicationError(
      'Failed to get resume URL',
      'URL_GENERATION_ERROR',
      { resumePath, originalError: error.message }
    );
  }
};

// ============================================
// APPLICATION SUBMISSION
// ============================================

/**
 * Submits a job application with full validation and error handling
 * @param {Object} applicationData - Application data
 * @returns {Promise<Object>} - Submission result
 */
export const submitApplication = async (applicationData) => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('User not authenticated');

    // Get profile summary for application
    const profileSummary = await profileService?.getProfileSummary(user?.id);

    // Prepare application data with profile information
    const snakeCaseData = {
      user_id: user?.id,
      job_id: applicationData?.jobId,
      cover_letter: applicationData?.coverLetter,
      status: 'pending',
      // Include profile data in application
      applicant_name: profileSummary?.fullName,
      applicant_email: profileSummary?.email,
      applicant_phone: profileSummary?.phone,
      applicant_resume_url: profileSummary?.resumeUrl,
      applicant_linkedin_url: profileSummary?.linkedinUrl,
      applicant_portfolio_url: profileSummary?.portfolioUrl,
      // Additional fields
      location_id: applicationData?.locationId,
      custom_responses: applicationData?.customResponses,
      source: applicationData?.source || 'direct',
      applied_at: new Date()?.toISOString(),
    };

    // Step 1: Validate input data
    const validation = validateApplicationData(snakeCaseData);
    if (!validation?.isValid) {
      throw new ApplicationError(
        'Invalid application data',
        'VALIDATION_ERROR',
        { errors: validation.errors }
      );
    }
    
    // Step 2: Check for duplicate application
    const isDuplicate = await checkDuplicateApplication(
      snakeCaseData?.job_id,
      snakeCaseData?.user_id
    );
    
    if (isDuplicate) {
      throw new ApplicationError(
        'You have already applied to this job',
        'DUPLICATE_APPLICATION',
        { 
          jobId: snakeCaseData.job_id,
          candidateId: snakeCaseData.user_id
        }
      );
    }
    
    // Step 3: Handle resume upload if file provided
    let resumePath = null;
    if (applicationData?.resume_file) {
      const uploadResult = await uploadResume(
        applicationData?.resume_file,
        snakeCaseData?.user_id
      );
      resumePath = uploadResult?.path;
    }
    
    // Step 4: Prepare application record
    const applicationRecord = {
      job_id: snakeCaseData?.job_id,
      candidate_id: snakeCaseData?.user_id,
      resume_path: resumePath,
      cover_letter: snakeCaseData?.cover_letter || null,
      status: snakeCaseData?.status,
      applied_at: snakeCaseData?.applied_at,
      additional_data: snakeCaseData?.additional_data || null
    };
    
    // Step 5: Insert application into database
    const { data, error } = await supabase?.from('job_applications')?.insert([applicationRecord])?.select(`
        *,
        jobs:job_id (
          id,
          title,
          company_id,
          companies:company_id (
            id,
            name
          )
        ),
        user_profiles:candidate_id (
          id,
          full_name,
          email
        )
      `)?.single();
    
    if (error) throw error;
    
    // Step 6: Send email notifications (non-blocking)
    try {
      const candidateEmail = data?.user_profiles?.email;
      const companyEmail = await getCompanyEmail(data?.jobs?.companies?.id);
      
      const emailData = {
        applicationId: data?.id,
        jobTitle: data?.jobs?.title,
        companyName: data?.jobs?.companies?.name,
        companyEmail: companyEmail,
        candidateName: data?.user_profiles?.full_name,
        candidateEmail: candidateEmail,
        coverLetter: data?.cover_letter,
        appliedAt: data?.applied_at
      };
      
      // Send emails asynchronously (don't wait for completion)
      sendApplicationNotifications(emailData)?.catch(emailError => {
        console.error('Failed to send application emails:', emailError);
        // Don't fail the application submission if emails fail
      });
    } catch (emailError) {
      console.error('Error preparing application emails:', emailError);
      // Continue even if email sending fails
    }
    
    // Step 7: Return success response
    return {
      success: true,
      data: {
        application: data,
        message: 'Application submitted successfully'
      }
    };
    
  } catch (error) {
    if (error instanceof ApplicationError) {
      return formatErrorResponse(error, 'submitApplication');
    }
    
    return formatErrorResponse(
      new ApplicationError(
        'Failed to submit application',
        'SUBMISSION_ERROR',
        { originalError: error.message }
      ),
      'submitApplication'
    );
  }
};

// ============================================
// APPLICATION RETRIEVAL
// ============================================

/**
 * Gets all applications for a specific candidate
 * @param {string} candidateId - Candidate ID
 * @param {Object} options - Query options (filters, sorting, pagination)
 * @returns {Promise<Object>} - Applications list
 */
export const getCandidateApplications = async (candidateId, options = {}) => {
  try {
    const { 
      status = null, 
      sortBy = 'applied_at', 
      sortOrder = 'desc',
      limit = 50,
      offset = 0
    } = options;
    
    let query = supabase?.from('job_applications')?.select(`
        *,
        jobs:job_id (
          id,
          title,
          employment_type,
          work_mode,
          locations,
          companies:company_id (
            id,
            name,
            logo_url
          )
        )
      `)?.eq('candidate_id', candidateId);
    
    // Apply status filter if provided
    if (status) {
      query = query?.eq('status', status);
    }
    
    // Apply sorting
    query = query?.order(sortBy, { ascending: sortOrder === 'asc' });
    
    // Apply pagination
    query = query?.range(offset, offset + limit - 1);
    
    const { data, error, count } = await query;
    
    if (error) throw error;
    
    return {
      success: true,
      data: {
        applications: data || [],
        count: count || 0,
        limit,
        offset
      }
    };
    
  } catch (error) {
    return formatErrorResponse(
      new ApplicationError(
        'Failed to fetch applications',
        'FETCH_ERROR',
        { candidateId, originalError: error.message }
      ),
      'getCandidateApplications'
    );
  }
};

/**
 * Gets a single application by ID with full details
 * @param {string} applicationId - Application ID
 * @returns {Promise<Object>} - Application details
 */
export const getApplicationById = async (applicationId) => {
  try {
    const { data, error } = await supabase?.from('job_applications')?.select(`
        *,
        jobs:job_id (
          id,
          title,
          description,
          employment_type,
          work_mode,
          locations,
          salary,
          companies:company_id (
            id,
            name,
            description,
            logo_url,
            website_url
          )
        )
      `)?.eq('id', applicationId)?.single();
    
    if (error) throw error;
    
    // Get signed URL for resume if exists
    if (data?.resume_path) {
      data.resume_url = await getResumeUrl(data?.resume_path);
    }
    
    return {
      success: true,
      data
    };
    
  } catch (error) {
    return formatErrorResponse(
      new ApplicationError(
        'Failed to fetch application details',
        'FETCH_ERROR',
        { applicationId, originalError: error.message }
      ),
      'getApplicationById'
    );
  }
};

// ============================================
// APPLICATION STATUS MANAGEMENT
// ============================================

/**
 * Updates application status
 * @param {string} applicationId - Application ID
 * @param {string} newStatus - New status value
 * @param {string} updatedBy - ID of user making the update
 * @returns {Promise<Object>} - Update result
 */
export const updateApplicationStatus = async (applicationId, newStatus, updatedBy) => {
  try {
    const validStatuses = ['applied', 'reviewed', 'interview', 'accepted', 'rejected'];
    
    if (!validStatuses?.includes(newStatus)) {
      throw new ApplicationError(
        'Invalid status value',
        'INVALID_STATUS',
        { providedStatus: newStatus, validStatuses }
      );
    }
    
    const { data, error } = await supabase?.from('job_applications')?.update({
        status: newStatus,
        updated_at: new Date()?.toISOString()
      })?.eq('id', applicationId)?.select()?.single();
    
    if (error) throw error;
    
    return {
      success: true,
      data: {
        application: data,
        message: `Application status updated to ${newStatus}`
      }
    };
    
  } catch (error) {
    if (error instanceof ApplicationError) {
      return formatErrorResponse(error, 'updateApplicationStatus');
    }
    
    return formatErrorResponse(
      new ApplicationError(
        'Failed to update application status',
        'UPDATE_ERROR',
        { applicationId, newStatus, originalError: error.message }
      ),
      'updateApplicationStatus'
    );
  }
};

// ============================================
// COMPANY-SIDE OPERATIONS
// ============================================

/**
 * Gets all applications for a specific job (company view)
 * @param {string} jobId - Job ID
 * @param {Object} options - Query options
 * @returns {Promise<Object>} - Applications list
 */
export const getJobApplications = async (jobId, options = {}) => {
  try {
    const { 
      status = null,
      sortBy = 'applied_at',
      sortOrder = 'desc',
      limit = 50,
      offset = 0
    } = options;
    
    let query = supabase?.from('job_applications')?.select(`
        *,
        user_profiles:candidate_id (
          id,
          full_name,
          email,
          profile_picture_url,
          phone
        )
      `)?.eq('job_id', jobId);
    
    if (status) {
      query = query?.eq('status', status);
    }
    
    query = query?.order(sortBy, { ascending: sortOrder === 'asc' });
    query = query?.range(offset, offset + limit - 1);
    
    const { data, error, count } = await query;
    
    if (error) throw error;
    
    // Get resume URLs for all applications
    const applicationsWithUrls = await Promise.all(
      (data || [])?.map(async (app) => {
        if (app?.resume_path) {
          try {
            app.resume_url = await getResumeUrl(app?.resume_path);
          } catch (error) {
            console.error(`Failed to get resume URL for application ${app?.id}:`, error);
            app.resume_url = null;
          }
        }
        return app;
      })
    );
    
    return {
      success: true,
      data: {
        applications: applicationsWithUrls,
        count: count || 0,
        limit,
        offset
      }
    };
    
  } catch (error) {
    return formatErrorResponse(
      new ApplicationError(
        'Failed to fetch job applications',
        'FETCH_ERROR',
        { jobId, originalError: error.message }
      ),
      'getJobApplications'
    );
  }
};

/**
 * Gets application statistics for a job
 * @param {string} jobId - Job ID
 * @returns {Promise<Object>} - Application statistics
 */
export const getApplicationStats = async (jobId) => {
  try {
    const { data, error } = await supabase?.from('job_applications')?.select('status')?.eq('job_id', jobId);
    
    if (error) throw error;
    
    const stats = {
      total: data?.length,
      applied: data?.filter(app => app?.status === 'applied')?.length,
      reviewed: data?.filter(app => app?.status === 'reviewed')?.length,
      interview: data?.filter(app => app?.status === 'interview')?.length,
      accepted: data?.filter(app => app?.status === 'accepted')?.length,
      rejected: data?.filter(app => app?.status === 'rejected')?.length
    };
    
    return {
      success: true,
      data: stats
    };
    
  } catch (error) {
    return formatErrorResponse(
      new ApplicationError(
        'Failed to fetch application statistics',
        'STATS_ERROR',
        { jobId, originalError: error.message }
      ),
      'getApplicationStats'
    );
  }
};

// Export all functions
export default {
  submitApplication,
  getCandidateApplications,
  getApplicationById,
  updateApplicationStatus,
  getJobApplications,
  getApplicationStats,
  checkDuplicateApplication,
  uploadResume,
  getResumeUrl
};